import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Plus, Building2, Edit2, Trash2, X, Users2, MapPin, User } from "lucide-react";
import styles from "../hrms.module.scss";
import { APIConstant } from "services/api-constant";
import Popup from "components/minor/popup/popup";
import { useTheme } from "context/ThemeContext";

const Schema = Yup.object({
  name: Yup.string().required("Department name is required"),
});

const DEPT_COLORS = ["#00C8FF","#7C5CFF","#00E5A3","#FFB800","#FF4D6D","#3B82F6","#A855F7","#10B981"];

export default function DepartmentManagement() {
  const { T } = useTheme();
  const [depts,   setDepts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [open,    setOpen]    = useState(false);
  const [mode,    setMode]    = useState("add");
  const [sel,     setSel]     = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [delId,   setDelId]   = useState(null);

  const fetchDepts = async () => {
    try { setLoading(true); const r = await axios.get(APIConstant.departments); setDepts(r.data||[]); }
    catch(e){ console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(()=>{ fetchDepts(); },[]);

  const handleSubmit = async (vals) => {
    try {
      if (mode==="edit"&&sel) await axios.put(`${APIConstant.departments}${sel.id}`, vals);
      else await axios.post(APIConstant.departments, vals);
      setOpen(false); fetchDepts();
    } catch(e){ console.error(e); }
  };

  const handleDelete = async () => {
    try { await axios.delete(`${APIConstant.departments}${delId}`); setDelOpen(false); fetchDepts(); }
    catch(e){ console.error(e); }
  };

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>EMPLOYEE MANAGEMENT</div>
          <h2>Departments</h2>
          <p>Manage company departments and their heads</p>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.btnPrimary} onClick={()=>{setMode("add");setSel(null);setOpen(true);}}>
            <Plus size={14}/> Add Department
          </button>
        </div>
      </div>

      {loading ? <div className={styles.loader}><div className={styles.spinner}/>Loading…</div> : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:16}}>
          {depts.map((d,i)=>(
            <div key={d.id} className={styles.card} style={{position:"relative",overflow:"hidden"}}>
              <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:DEPT_COLORS[i%DEPT_COLORS.length],borderRadius:"14px 14px 0 0"}}/>
              <div style={{padding:22}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
                  <div style={{width:44,height:44,borderRadius:12,background:`${DEPT_COLORS[i%DEPT_COLORS.length]}20`,display:"flex",alignItems:"center",justifyContent:"center",color:DEPT_COLORS[i%DEPT_COLORS.length]}}>
                    <Building2 size={20}/>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button className={styles.btnSm} onClick={()=>{setMode("edit");setSel(d);setOpen(true);}}>
                      <Edit2 size={12}/>
                    </button>
                    <button className={styles.btnSm} style={{color:T.red,borderColor:`${T.red}40`}}
                      onClick={()=>{setDelId(d.id);setDelOpen(true);}}>
                      <Trash2 size={12}/>
                    </button>
                  </div>
                </div>
                <h4 style={{fontSize:16,fontWeight:700,color:T.text,margin:"0 0 8px"}}>{d.name}</h4>
                {d.description && <p style={{fontSize:12.5,color:T.muted,margin:"0 0 14px",lineHeight:1.5}}>{d.description}</p>}
                <div style={{display:"flex",flexDirection:"column",gap:7}}>
                  {d.head_name && (
                    <div style={{display:"flex",alignItems:"center",gap:7,fontSize:12.5,color:T.muted}}>
                      <User size={12}/> Head: <span style={{color:T.text}}>{d.head_name}</span>
                    </div>
                  )}
                  {d.location && (
                    <div style={{display:"flex",alignItems:"center",gap:7,fontSize:12.5,color:T.muted}}>
                      <MapPin size={12}/> {d.location}
                    </div>
                  )}
                  <div style={{display:"flex",alignItems:"center",gap:7,fontSize:12.5,color:T.muted}}>
                    <Users2 size={12}/> <span style={{color:DEPT_COLORS[i%DEPT_COLORS.length],fontWeight:700,fontFamily:"JetBrains Mono,monospace"}}>{d.employee_count}</span> employees
                  </div>
                </div>
              </div>
            </div>
          ))}
          {depts.length===0 && (
            <div style={{gridColumn:"1/-1",textAlign:"center",padding:"60px 20px",color:T.muted,fontSize:14}}>
              No departments yet — click "Add Department" to create one.
            </div>
          )}
        </div>
      )}

      {/* Form Modal */}
      <Dialog open={open} onClose={()=>setOpen(false)}>
        <div style={{background:T.card,border:`1px solid ${T.cyan}33`,borderRadius:14,width:480,fontFamily:"Outfit,sans-serif",overflow:"hidden"}}>
          <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.border}`,background:`${T.cyan}0A`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <h3 style={{margin:0,fontSize:16,fontWeight:600,color:T.text}}>{mode==="add"?"Add":"Edit"} Department</h3>
            <button style={{background:"none",border:"none",cursor:"pointer",color:T.muted}} onClick={()=>setOpen(false)}><X size={16}/></button>
          </div>
          <div style={{padding:24}}>
            <Formik
              enableReinitialize
              initialValues={mode==="edit"&&sel
                ? {name:sel.name,head_name:sel.head_name||"",location:sel.location||"",description:sel.description||""}
                : {name:"",head_name:"",location:"",description:""}}
              validationSchema={Schema}
              onSubmit={handleSubmit}
            >
              {({errors,touched})=>(
                <Form>
                  {[
                    {name:"name",      label:"Department Name *", placeholder:"e.g. Engineering"},
                    {name:"head_name", label:"Department Head",   placeholder:"Manager name"},
                    {name:"location",  label:"Location",          placeholder:"City / Office"},
                  ].map(({name,label,placeholder})=>(
                    <div key={name} style={{marginBottom:16}}>
                      <label style={{fontSize:11.5,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.06em"}}>{label}</label>
                      <Field name={name} placeholder={placeholder}
                        style={{width:"100%",background:T.inp_bg,border:`1px solid ${errors[name]&&touched[name]?T.red:T.border}`,borderRadius:8,color:T.text,fontSize:13,padding:"9px 12px",outline:"none",fontFamily:"Outfit,sans-serif",boxSizing:"border-box"}}/>
                      {errors[name]&&touched[name]&&<p style={{color:T.red,fontSize:11.5,marginTop:4}}>{errors[name]}</p>}
                    </div>
                  ))}
                  <div style={{marginBottom:18}}>
                    <label style={{fontSize:11.5,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.06em"}}>Description</label>
                    <Field as="textarea" name="description" rows={2} placeholder="Brief description"
                      style={{width:"100%",background:T.inp_bg,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:13,padding:"9px 12px",outline:"none",fontFamily:"Outfit,sans-serif",resize:"vertical",boxSizing:"border-box"}}/>
                  </div>
                  <div style={{display:"flex",gap:10,justifyContent:"flex-end",paddingTop:12,borderTop:`1px solid ${T.border}`}}>
                    <button type="button" onClick={()=>setOpen(false)} className={styles.btnSecondary}>Cancel</button>
                    <button type="submit" className={styles.btnPrimary}>{mode==="add"?"Save":"Update"}</button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </Dialog>

      <Popup open={delOpen} message="Delete this department?" handleClose={()=>setDelOpen(false)} handleConfirm={handleDelete}/>
    </div>
  );
}
